import type { AccrualZeroReason, PortalCommissionRate } from '@/types/api'

/**
 * Why a line earned nothing, in the partner's own terms.
 *
 * A nil line is written into a statement with its reason rather than left out, because an
 * absent row is silence and silence is what generates the telephone call to finance. The
 * wording here is deliberately not the enum name: `BeyondFiveYearCap` is a rule, "past
 * year 5" is what happened to this payment.
 */
export function zeroReasonLabel(reason: AccrualZeroReason): string {
  switch (reason) {
    case 'RateIsZero':
      // Decided, not missing. A zero in the grid was typed by somebody; a blank cannot be
      // activated at all.
      return 'your rate for this year is 0%'
    case 'BeyondFiveYearCap':
      return 'past year 5'
    case 'NoRatesFrozen':
      return 'no rates were fixed on this deal'
    case 'NoAnchorDate':
      return 'the deal has no start date yet'
  }
}

/**
 * What to print in the rate column of a nil line.
 *
 * A rate of 0.00% and no rate at all are different facts, and printing the first for the
 * second would be inventing one. Past year five there is no rate to apply, and where the
 * deal froze no grid there was never one to begin with - so those two show a dash, and
 * only a genuine zero in the grid shows 0.00%.
 */
export function hasRate(reason: AccrualZeroReason | null): boolean {
  return reason === null || reason === 'RateIsZero'
}

/**
 * The contract years a plan actually carries, in order.
 *
 * Read off the rates rather than assumed to be one to five. Five is the cap the domain
 * enforces, but a grid that arrived with four years should render four columns rather than a
 * fifth that silently reads as "no rate" - the two are different facts and only one of them
 * is true.
 */
export function contractYears(rates: readonly PortalCommissionRate[]): number[] {
  return [...new Set(rates.map((rate) => rate.contractYear))].sort((a, b) => a - b)
}

/**
 * The products this grid names, in the order Codemax listed them.
 *
 * A grid does not have to mention every product. The cells with no product are the standard
 * rate, and anything not listed is paid at it - which is why a partner's grid can be three
 * rows long and still answer for the whole catalogue.
 */
export function productsOf(
  rates: readonly PortalCommissionRate[],
): { id: string; name: string }[] {
  const named = new Map<string, string>()

  for (const rate of rates) {
    if (rate.productId !== null) named.set(rate.productId, rate.productName ?? 'Product')
  }

  return [...named].map(([id, name]) => ({ id, name }))
}

/**
 * One cell of the grid, or `undefined` when the plan carries no such cell.
 *
 * The distinction is the whole point. A plan cannot be activated with a cell unfilled, so a
 * stored `0` means "not entitled" - a decision somebody typed - while a missing cell means
 * the grid is not what this screen assumes it is. Returning `0` for both would print a
 * confident zero over a gap, which is the one reading a partner cannot check.
 */
export function rateAt(
  rates: readonly PortalCommissionRate[],
  contractYear: number,
  billingKind: PortalCommissionRate['billingKind'],
  productId: string | null = null,
): number | undefined {
  return rates.find(
    (rate) =>
      rate.contractYear === contractYear &&
      rate.billingKind === billingKind &&
      (rate.productId ?? null) === productId,
  )?.percent
}
